import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MessageCircle, X } from "lucide-react";
import { api } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { whatsappLink } from "@/lib/site";
import { EASE } from "@/components/Reveal";

export default function PaymentResult({ success }) {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const { clearCart } = useShop();
  const [status, setStatus] = useState(success ? "checking" : "cancelled");
  const cleared = useRef(false);

  useEffect(() => {
    if (!success) return;
    if (!orderId) {
      setStatus("unknown");
      return;
    }
    let tries = 0;
    let interval;
    const tick = async () => {
      try {
        const { data } = await api.get(`/orders/status/${orderId}`);
        if (data.status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return true;
        }
      } catch (e) {}
      return false;
    };
    tick().then((paid) => {
      if (paid) return;
      interval = setInterval(async () => {
        tries += 1;
        const paid = await tick();
        if (paid || tries > 12) {
          clearInterval(interval);
          if (!paid) setStatus("pending");
        }
      }, 2500);
    });
    return () => clearInterval(interval);
  }, [success, orderId]);

  return (
    <main className="pt-32 pb-24 px-6">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mx-auto max-w-lg text-center card-surface p-10 sm:p-14"
        data-testid="payment-result-page"
      >
        {status === "paid" && (
          <>
            <span className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-gold text-[#241A08]">
              <Check size={34} />
            </span>
            <p className="overline-tag mt-6">Order Confirmed</p>
            <h1 className="mt-3 font-display text-4xl font-semibold">Thank you — truly.</h1>
            {orderId && <p className="mt-3 font-mono text-xs text-ink2">Order {orderId}</p>}
            <p className="mt-4 text-sm text-ink2 leading-relaxed">
              Your pieces are being prepared with their hallmarked certificate. Our concierge
              will message you on WhatsApp with delivery details.
            </p>
            <Link to="/shop" className="btn-gold mt-8">Continue Exploring</Link>
          </>
        )}

        {status === "pending" && (
          <>
            <p className="overline-tag">Confirming Payment</p>
            <h1 className="mt-3 font-display text-4xl font-semibold">Almost there…</h1>
            {orderId && <p className="mt-3 font-mono text-xs text-ink2">Order {orderId}</p>}
            <p className="mt-4 text-sm text-ink2 leading-relaxed">
              We&apos;re confirming your payment with the gateway. This page will update shortly —
              or message us and we&apos;ll confirm instantly.
            </p>
            <a
              href={whatsappLink(`Hello Sashti Jewels, I just paid for order ${orderId}. Please confirm.`)}
              target="_blank" rel="noopener noreferrer"
              className="btn-ghost-gold mt-8"
              data-testid="payment-pending-whatsapp"
            >
              <MessageCircle size={14} /> Confirm on WhatsApp
            </a>
          </>
        )}

        {status === "cancelled" && (
          <>
            <span className="mx-auto grid h-20 w-20 place-items-center rounded-full border border-gold text-gold">
              <X size={30} />
            </span>
            <p className="overline-tag mt-6">Payment Cancelled</p>
            <h1 className="mt-3 font-display text-4xl font-semibold">Your cart is safe</h1>
            <p className="mt-4 text-sm text-ink2 leading-relaxed">
              No money was taken. Your pieces are still waiting in the cart whenever you&apos;re ready.
            </p>
            <Link to="/checkout" className="btn-gold mt-8" data-testid="payment-cancel-retry">Return to Checkout</Link>
          </>
        )}

        {status === "unknown" && (
          <>
            <p className="overline-tag">Order Lookup</p>
            <h1 className="mt-3 font-display text-4xl font-semibold">We couldn&apos;t find that order</h1>
            <p className="mt-4 text-sm text-ink2 leading-relaxed">
              If you just paid, message us on WhatsApp and we&apos;ll confirm your order instantly.
            </p>
            <Link to="/shop" className="btn-gold mt-8">Back to the Atelier</Link>
          </>
        )}
      </motion.div>
    </main>
  );
}
